import { useMemo } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { useOsmTiles } from '../hooks/use-osm-tiles';
import { POI_BY_KEY, POI_MIN_ZOOM, iconSvg } from '../lib/poiCategories';

// POIs aus OpenStreetMap (Feuerwachen, Polizei, Rathaus, Gastronomie …) nach gewählten Kategorien.
// Das Backend lädt alle Kategorien einer Kachel auf einmal, gefiltert wird hier.

const MAX_MARKERS = 500;

const iconCache = new Map();

const poiIcon = (category) => {
  if (!iconCache.has(category.key)) {
    iconCache.set(category.key, L.divIcon({
      html: `<div class="poi-marker-dot" style="background:${category.color}">${iconSvg(category.iconNode, 14)}</div>`,
      className: 'poi-marker',
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12],
    }));
  }
  return iconCache.get(category.key);
};

const address = (tags) => {
  const street = [tags['addr:street'], tags['addr:housenumber']].filter(Boolean).join(' ');
  const city = [tags['addr:postcode'], tags['addr:city']].filter(Boolean).join(' ');
  return [street, city].filter(Boolean).join(', ');
};

const PoiPopup = ({ tags, category }) => (
  <div className="font-sans text-sm">
    <strong>{tags.name || category.label}</strong>
    {tags.name && <p className="text-xs text-muted-foreground">{category.label}</p>}
    {address(tags) && <p className="mt-1">{address(tags)}</p>}
    {tags.operator && <p className="mt-1">Betreiber: {tags.operator}</p>}
    {tags.phone && <p className="mt-1">Telefon: {tags.phone}</p>}
    {tags.opening_hours && <p className="mt-1">Öffnungszeiten: {tags.opening_hours}</p>}
    <p className="mt-2 text-xs text-muted-foreground">Quelle: OpenStreetMap</p>
  </div>
);

export const PoiLayer = ({ categories, onStatusChange }) => {
  const selected = useMemo(() => new Set(categories), [categories]);
  const pois = useOsmTiles({
    layer: 'poi',
    enabled: categories.length > 0,
    minZoom: POI_MIN_ZOOM,
    maxItems: MAX_MARKERS,
    filter: (item) => selected.has(item.tags.cat),
    filterKey: categories.join(','),
    onStatusChange,
  });

  if (categories.length === 0) return null;

  return pois.map(poi => {
    const category = POI_BY_KEY[poi.tags.cat];
    if (!category) return null;
    return (
      <Marker
        key={poi.id}
        position={[poi.lat, poi.lon]}
        icon={poiIcon(category)}
        // Taktische Zeichen sollen über den POIs liegen
        zIndexOffset={-1000}
        keyboard={false}
      >
        <Popup>
          <PoiPopup tags={poi.tags} category={category} />
        </Popup>
      </Marker>
    );
  });
};

export default PoiLayer;
